// Phase 9 — wird-saving coherence rules.
//
// Pure validator shared by:
//   - the Firestore sessionService (Phase 9.2)
//   - the al-bayan-backend POST /api/sessions + POST /api/wird-ratings handlers (Phase 9.3)
//   - the seedRealisticV2 generator + auditDemoConsistency (Phase 9.5/9.6)
//
// All inputs are plain JS arrays/objects so the same module loads under
// node --test, the React app, and the al-bayan-backend (via require).
//
// Definitions:
//   learned        = priorMemorized ∪ memorized (manual & auto subsets fold in)
//   everPrevSabak  = portionIds the student has ever been formally introduced to
//                    via SABAK / wirdRatings.kind='new' / MemorizationEdit bucket=prior
//
// Rules:
//   SABAK       — portion must NOT be in learned AND NOT in everPrevSabak
//   SABAQ_PARA  — portion must be in everPrevSabak
//   DHOR        — portion must be in learned

'use strict';

const SESSION_TYPES = Object.freeze({
  SABAK: 'SABAK',
  SABAQ_PARA: 'SABAQ_PARA',
  DHOR: 'DHOR',
});

const WIRD_KINDS = Object.freeze({
  NEW: 'new',
  REVISION: 'revision',
  CUSTOM: 'custom',
});

// SABAK ↔ wird `kind='new'`. SABAQ_PARA is short-revision so it pairs with
// wird `kind='custom'` (the legacy mapping). DHOR pairs with `kind='revision'`.
const SESSION_TYPE_TO_KIND = Object.freeze({
  SABAK: 'new',
  SABAQ_PARA: 'custom',
  DHOR: 'revision',
});

const KIND_TO_SESSION_TYPE = Object.freeze({
  new: 'SABAK',
  revision: 'DHOR',
  custom: 'SABAQ_PARA',
});

function toIntSet(arr) {
  const out = new Set();
  if (!arr) return out;
  for (const v of arr) {
    const n = typeof v === 'number' ? v : Number(v);
    if (Number.isFinite(n)) out.add(n | 0);
  }
  return out;
}

/**
 * Build the canonical "learned" + "everPrevSabak" sets from a student
 * record + their wird/session history. Pure — caller passes raw arrays
 * the validator expects.
 *
 * @param {Object} student
 * @param {number[]|string[]} student.priorMemorizedPortions
 * @param {number[]|string[]} student.memorizedPortions
 * @param {number[]|string[]} [student.autoMemorizedPortions]
 * @param {Object} history
 * @param {number[]|string[]} [history.sabakPortions]   — every portion ever filed as SABAK / kind=new for the student
 * @param {number[]|string[]} [history.priorMemorizationEdits] — portions added via MemorizationEdit bucket=prior
 */
function buildSets(student, history) {
  const prior = toIntSet(student && student.priorMemorizedPortions);
  const memorized = toIntSet(student && student.memorizedPortions);
  const auto = toIntSet(student && student.autoMemorizedPortions);
  const learned = new Set([...prior, ...memorized, ...auto]);
  const everPrevSabak = new Set([
    ...prior, // enrolment baseline counts as "previously seen"
    ...toIntSet(history && history.sabakPortions),
    ...toIntSet(history && history.priorMemorizationEdits),
  ]);
  return { learned, everPrevSabak };
}

/**
 * Validate a wird/session save BEFORE it's written.
 *
 * @param {Object}   args
 * @param {Object}   args.student              — student doc (priorMemorized, memorized, auto, …)
 * @param {string}   args.sessionType          — 'SABAK' | 'SABAQ_PARA' | 'DHOR'
 * @param {number[]} args.portionsAffected     — rub' ids 1..240
 * @param {Object}   args.history              — { sabakPortions: number[], priorMemorizationEdits?: number[] }
 * @returns {{ ok: boolean, errors: Array<{portionId:number, reason:string, code:string}> }}
 */
function validateWirdSave({ student, sessionType, portionsAffected, history }) {
  const errors = [];
  const portions = Array.isArray(portionsAffected) ? portionsAffected.map((p) => (typeof p === 'number' ? p : Number(p))) : [];
  if (!sessionType || !SESSION_TYPES[sessionType]) {
    return {
      ok: false,
      errors: [{ portionId: null, reason: `unknown sessionType: ${sessionType}`, code: 'BAD_SESSION_TYPE' }],
    };
  }
  if (portions.length === 0) {
    return { ok: true, errors: [] };
  }
  const { learned, everPrevSabak } = buildSets(student, history);

  for (const raw of portions) {
    const p = raw | 0;
    if (!Number.isFinite(p) || p < 1 || p > 240) {
      errors.push({ portionId: raw, reason: 'portion out of range 1..240', code: 'BAD_PORTION' });
      continue;
    }
    if (sessionType === SESSION_TYPES.SABAK) {
      if (learned.has(p)) {
        errors.push({ portionId: p, reason: 'لا يمكن تسجيل سبق على ربع محفوظ سابقاً', code: 'SABAK_ALREADY_LEARNED' });
        continue;
      }
      if (everPrevSabak.has(p)) {
        errors.push({ portionId: p, reason: 'لا يمكن تسجيل سبق على ربع سبق له أن سُجِّل سبقاً جديداً', code: 'SABAK_ALREADY_INTRODUCED' });
        continue;
      }
    } else if (sessionType === SESSION_TYPES.SABAQ_PARA) {
      if (!everPrevSabak.has(p)) {
        errors.push({ portionId: p, reason: 'لا يمكن مراجعة الماضي على ربع لم يُقرأ سبقاً جديداً من قبل', code: 'SABAQ_PARA_UNSEEN' });
        continue;
      }
    } else if (sessionType === SESSION_TYPES.DHOR) {
      if (!learned.has(p)) {
        errors.push({ portionId: p, reason: 'لا يمكن تسجيل السرد على ربع غير محفوظ', code: 'DHOR_NOT_LEARNED' });
        continue;
      }
    }
  }

  return { ok: errors.length === 0, errors };
}

/**
 * Compute the disabled-portion set for the QuranSelector UI given a
 * student + session type. Returns the portion ids the picker should
 * grey out + a tooltip code per portion.
 *
 * SABAK:      disabled = learned ∪ everPrevSabak
 * SABAQ_PARA: disabled = complement of everPrevSabak
 * DHOR:       disabled = complement of learned
 */
function computeDisabledPortions({ student, sessionType, history, totalPortions = 240 }) {
  const disabled = new Map(); // portionId -> code
  if (!sessionType || !SESSION_TYPES[sessionType]) return disabled;
  const { learned, everPrevSabak } = buildSets(student, history);
  for (let p = 1; p <= totalPortions; p += 1) {
    if (sessionType === SESSION_TYPES.SABAK) {
      if (learned.has(p)) disabled.set(p, 'SABAK_ALREADY_LEARNED');
      else if (everPrevSabak.has(p)) disabled.set(p, 'SABAK_ALREADY_INTRODUCED');
    } else if (sessionType === SESSION_TYPES.SABAQ_PARA) {
      if (!everPrevSabak.has(p)) disabled.set(p, 'SABAQ_PARA_UNSEEN');
    } else if (sessionType === SESSION_TYPES.DHOR) {
      if (!learned.has(p)) disabled.set(p, 'DHOR_NOT_LEARNED');
    }
  }
  return disabled;
}

module.exports = {
  SESSION_TYPES,
  WIRD_KINDS,
  SESSION_TYPE_TO_KIND,
  KIND_TO_SESSION_TYPE,
  validateWirdSave,
  computeDisabledPortions,
  buildSets,
  toIntSet,
};
